import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Card,
    CardContent,
    Divider,
    Chip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns/format';
import { tr } from 'date-fns/locale';
import { ParmakIziError } from '../../../types/ErrorModals/ParmakIziError';
import { getOgrenciParmakIzi } from '../../../services/api/parmakIziService';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NoMeetingRoomIcon from '@mui/icons-material/NoMeetingRoom';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, Tooltip } from '@mui/material';

const ParmakIzi: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { kullaniciModal } = useAuth();

    // Add state for selected season with default value 2025
    const [selectedSeason, setSelectedSeason] = useState<string>('2025');
    // Available seasons - same as in other components
    const availableSeasons = ['2023', '2024', '2025', '2026', '2027'];

    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['parmakIzi', kullaniciModal?.id, selectedSeason],
        queryFn: async () => {
            try {
                return await getOgrenciParmakIzi({
                    kurumKodu: localStorage.getItem('kurumKodu') || '',
                    ogrenciId: kullaniciModal?.id || '',
                    sezon: parseInt(selectedSeason)
                });
            } catch (err) {
                const parmakIziError = err as ParmakIziError;
                throw new Error(parmakIziError.message);
            }
        },
        enabled: !!kullaniciModal?.id,
        retry: 1,
        // Refresh data every 5 minutes for real-time updates
        refetchInterval: 5 * 60 * 1000
    });

    // Refetch data when season changes
    useEffect(() => {
        if (kullaniciModal?.id) {
            refetch();
        }
    }, [selectedSeason, refetch, kullaniciModal?.id]);

    // Function to determine if a passage is entry or exit
    const isEntryPassage = (gecis: string): boolean => {
        return gecis?.toLowerCase().includes('giriş') ||
            gecis?.toLowerCase().includes('giris');
    };

    // Function to handle manual refresh
    const handleRefresh = () => {
        refetch();
    };

    return (
        <>
            {/* Header with title and refresh button */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
            }}> 
                {/* Season Selector */}
                <Box sx={{ mb: 3, mt: 2 }}>
                    <FormControl
                        variant="outlined"
                        size="small"
                        sx={{
                            minWidth: 200,
                            '& .MuiOutlinedInput-root': {
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                            }
                        }}
                    >
                        <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                        <Select
                            labelId="season-select-label"
                            id="season-select"
                            value={selectedSeason}
                            onChange={handleSeasonChange}
                            label="Eğitim Sezonu"
                        >
                            {availableSeasons.map((season) => (
                                <MenuItem key={season} value={season}>
                                    {season} - {(parseInt(season) + 1)} Eğitim Yılı
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Tooltip title="Yenile">
                    <IconButton
                        onClick={handleRefresh}
                        color="primary"
                        sx={{
                            '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                    >
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block">
                                CorrelationId: {(error as ParmakIziError).correlationId}
                            </Typography>
                        )
                    }
                >
                    {(error as Error).message}
                </Alert>
            ) : !data?.length ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {selectedSeason} sezonunda kayıtlı geçiş kaydı bulunmamaktadır.
                </Alert>
            ) : (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    {/* Table view for larger screens */}
                    {!isMobile ? (
                        <TableContainer component={Paper} sx={{ minWidth: 800, boxShadow: 3 }}>
                            <Table aria-label="parmak izi geçiş tablosu">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Tarih
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Saat
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Geçiş Tipi
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Geçiş No
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Cihaz
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((parmakIzi, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                                                "&:hover": {
                                                    backgroundColor: "rgba(232, 137, 16, 0.1)",
                                                    transition: "background-color 0.3s ease",
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                {format(new Date(parmakIzi.q_tarih), 'dd MMMM yyyy', { locale: tr })}
                                            </TableCell>
                                            <TableCell>{parmakIzi.q_saat}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={isEntryPassage(parmakIzi.q_gecis) ? <MeetingRoomIcon /> : <NoMeetingRoomIcon />}
                                                    label={parmakIzi.q_gecis}
                                                    color={isEntryPassage(parmakIzi.q_gecis) ? "success" : "error"}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{parmakIzi.q_gecisno}</TableCell>
                                            <TableCell>{parmakIzi.q_cihaz}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        // Card view for mobile
                        <Box>
                            {data.map((parmakIzi, index) => (
                                <Card
                                    key={index}
                                    sx={{
                                        mb: 2,
                                        boxShadow: 3,
                                        "&:hover": {
                                            boxShadow: 6,
                                            transition: "box-shadow 0.3s ease",
                                        },
                                        borderLeft: isEntryPassage(parmakIzi.q_gecis) ?
                                            '4px solid #4caf50' : '4px solid #f44336'
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" color="primary">
                                                {isEntryPassage(parmakIzi.q_gecis) ?
                                                    <MeetingRoomIcon color="success" sx={{ mr: 1, verticalAlign: 'middle' }} /> :
                                                    <NoMeetingRoomIcon color="error" sx={{ mr: 1, verticalAlign: 'middle' }} />
                                                }
                                                {parmakIzi.q_gecis}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Cihaz: {parmakIzi.q_cihaz}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body1">
                                            {format(new Date(parmakIzi.q_tarih), 'dd MMMM yyyy', { locale: tr })} - {parmakIzi.q_saat}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Geçiş No: {parmakIzi.q_gecisno}
                                        </Typography>
                                        {parmakIzi.q_sinif && (
                                            <Typography variant="body2" color="text.secondary">
                                                Sınıf: {parmakIzi.q_sinif}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

export default ParmakIzi;